import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import DialogModal from '../../../components/DialogModal';
import CustomDatePicker from '../../../components/DatePicker';
import DropdownSelect from '../../../components/DropdownSelect';

import {
  AttendanceSource,
  StaffAttendanceModalProps,
  StaffAttendanceView
} from '../types';
import {
  SnackbarSeverityType,
  StaffAttendanceStatus as StaffAttendanceOptions
} from '../../../enums';
import { StaffAttendanceStatus } from '../types';
import {
  AddWrapper,
  DateWrapper,
  InactiveBadge,
  profileImgStyle,
  ProfileWrapper
} from '../styled';
import { camelCaseToTitle } from '../../../utils/caseConversion';
import { RootState } from '../../../redux/store';
import { getStaffAttendanceRoles } from '../../Staff/config/constant';
import userApi from '../../../apis/users';
import staffAttendanceApis from '../../../apis/staffAttendance';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { defaultMappedRole } from '../../../config/schoolConfig';

const StaffAttendanceModal: React.FC<StaffAttendanceModalProps> = ({
  open,
  onClose,
  mode,
  selectedStaffAttendanceData,
  showToast,
  fetchStaffAttendanceData,
  searchDate
}) => {
  const isEdit = mode === 'edit';
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const [attendanceDate, setAttendanceDate] = useState<Dayjs | null>(
    dayjs(selectedStaffAttendanceData?.attendanceDate) ?? null
  );
  const [attendanceStatus, setAttendanceStatus] = useState<
    StaffAttendanceStatus | ''
  >(selectedStaffAttendanceData?.status ?? '');

  const [remarks, setRemarks] = useState<string>(
    selectedStaffAttendanceData?.remarks ?? ''
  );

  const [schoolStaffMemberId, setSchoolStaffMemberId] = useState<string>('');

  const [schoolStaffs, setSchoolStaffs] = useState([]);
  const [role, setRole] = useState<string>('');

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const statusOptions = useMemo(
    () =>
      Object.entries(StaffAttendanceOptions).map(([key, value]) => ({
        value: key,
        label: key === 'HALF_DAY_PRESENT' ? 'HALF DAY' : value.replace(/_/g, ' ')
      })),
    []
  );

  const sortedStaffs = useMemo(() => {
    return [...schoolStaffs].sort((a: any, b: any) =>
      a.name.localeCompare(b.name)
    );
  }, [schoolStaffs]);

  const staffOptions = useMemo(
    () =>
      sortedStaffs.map((staff: any) => ({
        inputValue: staff.name,
        label: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{staff.name}</Typography>
            {staff.status === 'IN_ACTIVE' && (
              <InactiveBadge>
                Inactive
              </InactiveBadge>
            )}
          </Box>
        ),
        value: staff.id
      })),
    [sortedStaffs]
  );

  useEffect(() => {
    if (open) {
      if (isEdit && selectedStaffAttendanceData) {
        setAttendanceStatus(selectedStaffAttendanceData.status ?? '');

        const dateToDisplay = selectedStaffAttendanceData.attendanceDate
          ? dayjs(selectedStaffAttendanceData.attendanceDate)
          : searchDate;

        setAttendanceDate(dateToDisplay);
        setRemarks(selectedStaffAttendanceData.remarks ?? '');
      } else {
        setRole('');
        setAttendanceStatus('');
        setRemarks('');
        setSchoolStaffMemberId('');
        setAttendanceDate(null);
      }
    }
  }, [open, isEdit, selectedStaffAttendanceData, searchDate]);

  const upsertAttendanceData = async (
    attendanceData: StaffAttendanceView[]
  ) => {
    setIsLoading(true);
    try {
      const formattedDate = attendanceDate?.format('YYYY-MM-DD') ?? '';
      const response: any = await staffAttendanceApis.upsertStaffAttendanceData(
        {
          school: loggedInUser.school,
          date: formattedDate,
          staffAttendanceData: attendanceData,
          source: AttendanceSource.ADMIN
        }
      );
      if (response.success) {
        await fetchStaffAttendanceData(false);
        showToast(
          `${isEdit ? 'Updated' : 'Added'} staff attendance data successfully`,
          SnackbarSeverityType.success
        );
      }
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message ||
        `Error ${isEdit ? 'updating' : 'adding'} staff attendance data`,
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const fetchStaffUsers = async (selectedRole: string) => {
    if (!selectedRole) return;
    try {
      const response = await userApi.fetchUsersByRoleAndName({
        role: selectedRole
      });
      setSchoolStaffs(response.data || response);
      if (isEmpty(response.data)) {
        showToast('No staff available', SnackbarSeverityType.error);
      }
      showToast('Fetched staffs', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message || 'Error in fething staffs',
        SnackbarSeverityType.error
      );
    }
  };

  const handleStatusChange = (newStatus: StaffAttendanceStatus) => {
    setAttendanceStatus(newStatus);
  };

  const handleSubmit = () => {
    let payload: StaffAttendanceView[];
    if (isEdit && selectedStaffAttendanceData) {
      payload = [
        {
          ...selectedStaffAttendanceData,
          status: attendanceStatus as StaffAttendanceStatus,
          markedAs: attendanceStatus as StaffAttendanceOptions,
          remarks: remarks
        }
      ];
    } else {
      payload = [
        {
          markedAs: attendanceStatus as StaffAttendanceOptions,
          userId: schoolStaffMemberId,
          attendanceDate: attendanceDate,
          remarks: remarks
        }
      ];
    }
    upsertAttendanceData(payload);
  };

  const isInvalid = isEdit
    ? !attendanceStatus
    : !attendanceDate || !attendanceStatus || !schoolStaffMemberId;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Attendance' : 'Add Attendance Record'}
      maxWidth='sm'
      actions={
        <>
          <Button variant='outlined' onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='contained'
            disabled={isInvalid || isLoading}
            onClick={handleSubmit}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : isEdit ? (
              'Save Changes'
            ) : (
              'Add Record'
            )}
          </Button>
        </>
      }
    >
      <AddWrapper>
        {isEdit ? (
          <ProfileWrapper>
            <img
              src={
                selectedStaffAttendanceData?.profile &&
                  selectedStaffAttendanceData.profile !== ''
                  ? selectedStaffAttendanceData.profile
                  : undefined
              }
              alt=''
              style={profileImgStyle}
            />
            <Box>
              <Typography variant='h6' lineHeight={1.2}>
                {selectedStaffAttendanceData?.name}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {camelCaseToTitle(
                  selectedStaffAttendanceData?.displayRole ??
                  defaultMappedRole[
                  selectedStaffAttendanceData?.role as unknown as string
                  ] ??
                  selectedStaffAttendanceData?.role ??
                  ''
                )}
              </Typography>
            </Box>
          </ProfileWrapper>
        ) : (
          <AddWrapper>
            <DropdownSelect
              name='role'
              required
              label='Role'
              value={role}
              options={getStaffAttendanceRoles(loggedInUser?.school)}
              onChange={(e) => {
                const newRole = e.target.value as string;
                setRole(newRole);
                fetchStaffUsers(newRole);
              }}
            />
            <DropdownSelect
              required
              name='staffMemberId'
              disabled={!role}
              label='Select Staff Member'
              value={schoolStaffMemberId}
              options={staffOptions}
              onChange={(e) => setSchoolStaffMemberId(e.target.value as string)}
              hasTopLabel
              isSearchable
            />
          </AddWrapper>
        )}

        <DateWrapper>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={{
              fieldMonthPlaceholder: () => 'MMM'
            }}
          >
            <CustomDatePicker
              required
              disabled={isEdit}
              label='Date'
              format='DD MMM, YYYY'
              value={attendanceDate}
              onChange={setAttendanceDate}
            />
          </LocalizationProvider>
        </DateWrapper>

        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            name='status'
            required
            label='Attendance Status'
            value={attendanceStatus}
            options={statusOptions.filter((option) => option.value !== 'All')}
            onChange={(e) =>
              handleStatusChange(e.target.value as StaffAttendanceStatus)
            }
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <TextInputWithIcon
            label='Remarks'
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </Box>
      </AddWrapper>
    </DialogModal>
  );
};

export default StaffAttendanceModal;
